import { useAppSelector } from '../../../../store/hooks/StoreHooks';
import useCollapseFacade from '../../../../facade/collapse.Facade';
import Accordion from '../../../common/accordion';
import LABELS from '../../../../constants/labels/motor.labels';
import MotorPerformanceGraph from '../charts/motor-performance/index'
import Select from '../../../common/select';
import useMotorFacade from '../../../../facade/calculation/calculation-output/motor-facade';
import { useRef } from 'react';
import html2canvas from "html2canvas";
import HelpCircle from '../../../../assets/icons/HelpCircle';
import SelectCreate from '../../../common/select-create';
import { showToast } from "../../../../utilities/commonUtilities";

const MotorPerformance = () => {
    const { motorPerformanceCollapse } = useAppSelector((state) => state?.collapse);
    const { tab1Input } = useAppSelector((state) => state?.tubing);
    const { MotorperformanceList, SelectedMotor, MotorInput, MotorPerformancePlotData } = useAppSelector(state => state?.motor)
    let performanceChartConfig: any;

    const motorChartRef = useRef<HTMLDivElement | null>(null);
    const createPerformanceChartConfig = (data: any) => {
        const yAxisMin = 0.5;
        const yAxisMax = 1.1;
        performanceChartConfig = {
            seriesDefaults: {
                type: "scatterLine"
            },
            legend: {
                position: "bottom",
                labels: {
                    font: "15px 16px Mulish",
                    color: "#fff"
                }
            },
            chartArea: {
                width: 900,
                height: 800
            },
            xAxis: [{
                min: 0.4,
                name: "PU_Motor_Horsepower",
                title: {
                    text: "PU Motor Horsepower",
                    margin: 20
                },
                majorGridLines: {
                    width: 0.8,
                    color: "#A0A0A0",
                },
                minorGridLines: {
                    width: 0.3,
                    color: "#A0A0A0",
                    visible: true
                }
            }],
            series: [
                {
                    name: "Voltage",
                    type: "scatterLine",
                    data: data?.samples,
                    xField: "hp",
                    yField: "voltage",
                    color: "#204887",
                    style: "smooth",
                    markers: {
                        visible: true
                    },
                    width: 4.0,
                    tooltip: {
                        visible: true,
                        format: "PUHP: {0}, Voltage: {1}"
                    }
                },
                {
                    name: "Current",
                    type: "scatterLine",
                    data: data?.samples,
                    xField: "hp",
                    yField: "current",
                    color: "#f97316",
                    style: "smooth",
                    markers: {
                        visible: true
                    },
                    width: 4.0,
                    tooltip: {
                        visible: true,
                        format: "PUHP: {0}, Current: {1}"
                    }
                },
                {
                    name: "Efficiency",
                    type: "scatterLine",
                    data: data?.samples,
                    xField: "hp",
                    yField: "efficiency",
                    color: "#188a42",
                    style: "smooth",
                    markers: {
                        visible: true
                    },
                    width: 4.0,
                    tooltip: {
                        visible: true,
                        format: "PUHP: {0}, Efficiecy: {1}"
                    }
                },
                {
                    name: "RPM",
                    type: "scatterLine",
                    data: data?.samples,
                    xField: "hp",
                    yField: "rpm",
                    color: "#5c2f88",
                    style: "smooth",
                    yAxis: "RPM",
                    markers: {
                        visible: true
                    },
                    width: 4.0,
                    tooltip: {
                        visible: true,
                        format: "PUHP: {0}, RPM: {1}"
                    }
                },
                {
                    name: "Power Factor",
                    type: "scatterLine",
                    data: data?.samples,
                    xField: "hp",
                    yField: "powerFactor",
                    color: "#059bb4",
                    style: "smooth",
                    markers: {
                        visible: true
                    },
                    width: 4.0,
                    tooltip: {
                        visible: true,
                        format: "PUHP: {0}, Power Factor: {1}"
                    }
                },
                {
                    name: "Target",
                    type: "scatterLine",
                    data: [{ hp: data?.targetMotorLoad, target: yAxisMin }, { hp: data?.targetMotorLoad, target: yAxisMax }],
                    xField: "hp",
                    yField: "target",
                    color: "black",
                    yAxis: "PU_Amps_Volts_PowerFactor_Efficiency",
                    markers: {
                        visible: true
                    },
                    width: 2.0,
                    tooltip: {
                        visible: true,
                        format: "{0}"
                    },
                    dashType: "solid",
                    visibleInLegend: false
                },
            ],
            yAxis: [
                {
                    name: "PU_Amps_Volts_PowerFactor_Efficiency",
                    title: {
                        text: "PU Amps, Volts, Power Factor, Efficiency",
                        margin: 20
                    },
                    min: yAxisMin,
                    max: yAxisMax,
                    color: "#A0A0A0",
                    minorGridLines: {
                        width: 0.3,
                        color: "#A0A0A0",
                        visible: true
                    },
                    majorGridLines: {
                        width: 0.8,
                        color: "#A0A0A0",
                    },
                },
                {
                    name: "RPM",
                    title: {
                        text: "RPM",
                        margin: 20
                    },
                    majorUnit: 80,
                    color: "#A0A0A0",
                    minorGridLines: {
                        width: 0.3,
                        color: "#A0A0A0",
                        visible: true
                    },
                    majorGridLines: {
                        width: 0.8,
                        color: "#A0A0A0",
                        visible: false
                    },
                }
            ],
            tooltip: {
                visible: true,
                format: "{0} {1}",
            }
        }
    }

    const data = MotorPerformancePlotData;
    createPerformanceChartConfig(data);

    const { updateMotorPerformanceCollapse } = useCollapseFacade();
    const handleMotorChartDownload = () => {
        if (motorChartRef.current) {
            html2canvas(motorChartRef.current, {
                backgroundColor: "#253040",
            }).then((canvas) => {
                const imgUrl = canvas.toDataURL("image/png");

                const link = document.createElement("a");
                link.href = imgUrl;
                link.download = "Motor Performance Plots.png";
                link.click();
            });
        }
    };
    const { updateMotorRating } = useMotorFacade();
    const handleSelectChange = (input:any, name:any) => {
      let value = input;
      if (value && typeof value === "object" && "value" in value) {
        value = value.value;
      }
      else if (value && typeof value === "object" && "target" in value && value.target.value !== undefined) {
        value = value.target.value;
      }
      if (value === "" || /^\d+$/.test(value)) {
        updateMotorRating(value);
      } else {
      showToast('Please enter numbers only.', 'error');
      }
    };
      const handleClearMotorRating = () => {
        updateMotorRating("");
      }
      const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      updateMotorRating(value);
    };
    const motorDeratingPercentList = [
        { value: '105', label: (105).toString() },
        { value: '100', label: (100).toString() },
        { value: '90', label: (90).toString() },
        { value: '80', label: (80).toString() },
        { value: '70', label: (70).toString() },
        { value: '60', label: (60).toString() },
        { value: '50', label: (50).toString() },
    ];

    const handleCollapseChange = (key: boolean) => {
        updateMotorPerformanceCollapse(key);
    };
    return (
      <Accordion
        tooltipText='Formulas'
        title='Motor performance data'
        link={"/help?tab=Motor#MotorPerformanceCal"}
        isOpen={motorPerformanceCollapse}
        onToggle={() => handleCollapseChange(!motorPerformanceCollapse)}
        content={
          <div className='text-neutral-25'>
            <div className='flex justify-center items-start p-4 gap-4 w-full'>
              <div className='flex flex-col justify-between items-center w-1/2 align-top'>
                <div className=' justify-between items-center w-full'>
                  <label className='text-[18px]'>{SelectedMotor?.name}</label>
                </div>
              </div>
              <div className='flex flex-col justify-between items-center w-1/2 align-top'></div>
              <div className='flex flex-col w-1/2'>
                <div className='flex items-center'>
                  <label className='text-[18px]'>{LABELS.MotorRating}</label>
                  <a
                    className='ml-1 text-blue-600 hover:text-blue-800'
                    href='/help?tab=Motor#MotorDeratingCal'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <HelpCircle className='w-5 h-5 cursor-pointer' />
                  </a>
                </div>
                {/* other input/fields can go here */}
              </div>
            </div>

            <div className='flex justify-center items-start p-4 gap-4 w-full pt-0'>
              <div className='flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2 align-top'>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Efficiency}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.efficiency}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.MotorLoadNP}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.motorLoad}%
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.OperatingTemp}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.opeatngTemp}{" "}
                    {tab1Input?.actNameMotor_TOperatingTemp}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Current}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.current}{" "}
                    {tab1Input?.actNameMotor_TCurrent}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Voltage}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.voltage}{" "}
                    {tab1Input?.actNameMotor_TVoltage}{" "}
                  </label>
                </div>
              </div>
              <div className='flex flex-col justify-between items-center w-1/2 align-top border-[1px] border-darkcolor-400 rounded-[8px] '>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.PowerFfactor}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.powerFactor}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.kw}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.kwOut}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Speed}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.speed} rpm
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Power}
                  </label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.changedHP} hp
                  </label>
                </div>
              </div>
              <div className='flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2 align-top'>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25 text-[14px] font-normal'>
                    {LABELS.Rating}
                  </label>
                  <SelectCreate
                    options={motorDeratingPercentList}
                    onChange={handleSelectChange}
                    padding="px-[14px] py-[5px]"
                    fontSize="16px"
                    className="w-1/2 text-neutral-25"
                    selectedValue={MotorInput?.motorLoad ?? ""}
                    name="MotorDeratingPercent"
                    placeholder="Select motor derating percent"
                    targetName="MotorDeratingPercent"
                    isSearch={true} 
                    showCloseIcon={true} 
                    onChangeSearch={handleSearchChange} 
                    onClear={handleClearMotorRating} 
                    disabled={false} 
                    textInput={true}
                  />
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25'>{LABELS.hp}</label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.motorHP}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25'>{LABELS.volts}</label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.motorVoltage}
                  </label>
                </div>
                <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                  <label className='text-neutral-25'>{LABELS.amps}</label>
                  <label className='text-neutral-25'>
                    {MotorperformanceList[0]?.motorCurrent}
                  </label>
                </div>
              </div>
            </div>
            <div className='flex justify-center items-start p-4 pt-0 gap-4 w-full'>
              <div className='flex flex-col justify-between items-center w-full'>
                <div className='flex justify-between items-center w-full'>
                  <label className='text-neutral-25 text-[18px] font-normal'>
                    {LABELS.PerformacePlot} {SelectedMotor?.name ?? ""}
                  </label>
                  <button
                    onClick={handleMotorChartDownload}
                    className='ml-[16px] border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25'
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>

            <div
              className='flex justify-center items-start p-2 pt-0 gap-4 w-full'
              ref={motorChartRef}
            >
              {/* <div>No data available</div> */}
              <MotorPerformanceGraph
                data={performanceChartConfig}
                motor={SelectedMotor?.name ?? ""}
              ></MotorPerformanceGraph>
            </div>
          </div>
        }
      />
    );
}

export default MotorPerformance